import React, {Fragment} from "react";
import {Paper, Select, MenuItem, Checkbox, FormControlLabel, Grid, Button} from "@mui/material";
import {styled} from "@mui/material/styles";
import {v4} from "uuid";
import {modSum} from "../_signals/s_char_calc";
import {s_char} from "../_signals/s_char";
import {s_chars} from "../_signals/s_chars";
import ccAttribute from "../_helper/ccAttribute";
import {IRoller} from "../_interfaces/IRoller";

const Item = styled("div")(({theme}) => ({
    backgroundColor: '#fff', ...theme.typography.body2,
    padding: 4,
    textAlign: 'left',
    color: '#000',
}));

interface RollerContentProps {
    roller: IRoller;
    setRoller: (roller: IRoller) => void;
    vs: string;
    setvs: (vs: string) => void;
    vstarget: string;
    setvstarget: (vstarget: string) => void;
    toggleAs: (as: string, e: any) => void;
    setTargetOpen: (open: boolean) => void;
    targets: string[];
}

const RollerContent: React.FC<RollerContentProps> = ({
                                                         roller,
                                                         setRoller,
                                                         vs,
                                                         setvs,
                                                         vstarget,
                                                         setvstarget,
                                                         toggleAs,
                                                         setTargetOpen,
                                                         targets
                                                     }) => {
    const mod_31 = [...Array(31).keys()];

    return (
        <Paper style={{padding: 8}}>
            <Grid container>
                <Grid item xs={12} md={6}>
                    <Item>{roller.roll.title}</Item>
                    <Item>Stufe {roller.roll.step}</Item>
                    <Item>Attribut {ccAttribute(roller.roll.attribute, 'tt')}</Item>
                    {roller.roll.vs ? <Item>vs {roller.roll.vs}</Item> : null}
                    {roller.roll.vsnum ? <Item>Mindestwurf {roller.roll.vsnum}</Item> : null}
                    {roller.roll.strain ? <Item>Überanstrengung {roller.roll.strain}</Item> : null}
                </Grid>
                <Grid item xs={12} md={3}>
                    <Item>Modifikator</Item>
                    <Item>
                        {!roller.rolled ? (
                            <Select value={roller.roll.step_mod || 0}
                                    onChange={(e) => setRoller({
                                        ...roller, roll: {...roller.roll, step_mod: e.target.value as number}
                                    })}
                                    variant="standard"
                            >
                                {mod_31.map(m => <MenuItem key={v4()} value={m}>{m}</MenuItem>)}
                            </Select>
                        ) : roller.roll.step_mod}
                    </Item>
                </Grid>

                {!roller.rolled && !roller.roll.as_fix ?
                    <Grid item xs={12} md={9}>
                        <Grid container>
                            <Grid item xs={12} md={2}>
                                <Item>
                                    <FormControlLabel style={{marginLeft: 0}}
                                                      label="Inititive?"
                                                      control={
                                                          <Checkbox
                                                              checked={roller.roll.as === 'ini' || false}
                                                              onClick={(e) => toggleAs('ini', e)}
                                                          />
                                                      }
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Item>
                                    <FormControlLabel style={{marginLeft: 0}}
                                                      label="Erholungsprobe?"
                                                      control={
                                                          <Checkbox
                                                              checked={roller.roll.as === 'ep' || false}
                                                              onClick={(e) => toggleAs('ep', e)}
                                                          />
                                                      }
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Item>
                                    <FormControlLabel
                                        style={{marginLeft: 0}}
                                        label="Schadenswurf?"
                                        control={
                                            <Checkbox
                                                checked={roller.roll.as === 'dmg' || false}
                                                onClick={(e) => toggleAs('dmg', e)}
                                            />
                                        }
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Item>
                                    <FormControlLabel style={{marginLeft: 0}}
                                                      label="Niederschlagsprobe?"
                                                      control={
                                                          <Checkbox
                                                              checked={roller.roll.as === 'nsp' || false}
                                                              onClick={(e) => toggleAs('nsp', e)}
                                                          />
                                                      }
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Item>
                                    <FormControlLabel
                                        style={{marginLeft: 0}}
                                        label="Trefferwurf?"
                                        control={
                                            <Checkbox
                                                checked={roller.roll.as === 'hit' || false}
                                                onClick={(e) => toggleAs('hit', e)}
                                            />
                                        }
                                    />
                                </Item>
                            </Grid>
                            {['nsp'].includes(roller.roll.as) ? <Grid item xs={12} md={5}>
                                <Item>Mindestwurf</Item>
                                <Item>
                                    <Select value={roller.roll.vsnum}
                                            style={{width: "100%"}}
                                            onChange={(e) => setRoller({
                                                ...roller, roll: {...roller.roll, vsnum: e.target.value as number}
                                            })}
                                            variant="standard"
                                    >
                                        {mod_31.map(m => <MenuItem key={v4()} value={m}>{m}</MenuItem>)}
                                    </Select>
                                </Item>
                            </Grid> : null}
                            {roller.roll.as === 'hit' ?
                                <Grid container>
                                    <Grid item xs={6} md={3}>
                                        <Item>Gegen</Item>
                                        <Item>
                                            <Select value={vs}
                                                    style={{width: "100%"}}
                                                    onChange={(e) => setvs(e.target.value)}
                                                    variant="standard"
                                            >
                                                <MenuItem key={v4()} value={''}>-</MenuItem>
                                                <MenuItem key={v4()} value={'kv'}>KV</MenuItem>
                                                <MenuItem key={v4()} value={'mv'}>MV</MenuItem>
                                                <MenuItem key={v4()} value={'sv'}>SV</MenuItem>
                                            </Select>
                                        </Item>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        {vs ? <Fragment>
                                            <Item>Ziel/e</Item>
                                            <Item>
                                                <Select value={vstarget}
                                                        style={{width: "100%"}}
                                                        onChange={(e) => setvstarget(e.target.value)}
                                                        variant="standard"
                                                >
                                                    <MenuItem key={v4()} value={''}>-</MenuItem>
                                                    <MenuItem key={v4()} value={'s'}>selbst</MenuItem>
                                                    <MenuItem key={v4()} value={'z'}>einzelnes Ziel</MenuItem>
                                                    <MenuItem key={v4()} value={'e'}>einzelne Ziele</MenuItem>
                                                    <MenuItem key={v4()} value={'g'}>höchste {vs} der
                                                        Ziele</MenuItem>
                                                    <MenuItem key={v4()} value={'g+'}>höchste {vs} der Ziele +1 für
                                                        jedes
                                                        zusätzliche Ziel</MenuItem>
                                                </Select>
                                            </Item>
                                        </Fragment> : null}
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        {vs && ['z', 'e', 'g', 'g+'].includes(vstarget) ? <Item>
                                            <Button onClick={() => setTargetOpen(true)}>auswählen</Button>
                                        </Item> : null}
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        {Array.from(s_chars.value.entries()).map(([, char]) => {
                                            if (targets.includes(char.id))
                                                return (
                                                    <Item>{char.name}</Item>
                                                )
                                            else return null
                                        })}
                                    </Grid>
                                </Grid> : null}
                        </Grid>
                    </Grid>
                    : null
                }

                {roller.rolled && !roller.roll.as_fix ?
                    <Grid item xs={5} md={9}>
                        {roller.roll.as === 'ini' ? <Item>als Intiative </Item> : null}
                        {roller.roll.as === 'ep' ? <Item>als Erholungsprobe </Item> : null}
                        {roller.roll.as === 'nsp' ? <Item>als Niederschlagsprobe </Item> : null}
                        {roller.roll.as === 'dmg' ? <Item>als Schadenswurf </Item> : null}
                        {roller.roll.as === 'hit' ? <Item>
                            <div>als Trefferwurf</div>
                            <div>
                                {vs == 'kv' ? 'gegen KV' : null}
                                {vs == 'mv' ? 'gegen MV' : null}
                                {vs == 'sv' ? 'gegen SV' : null}
                            </div>
                            <div>
                                {vstarget == 's' ? 'selbst' : null}
                                {vstarget == 'z' ? 'einzelnes Ziel' : null}
                                {vstarget == 'e' ? 'einzelne Ziele' : null}
                                {vstarget == 'g' ? 'höchstes ' + vs + ' der Ziele' : null}
                                {vstarget == 'g+' ? 'höchste ' + vs + ' der Ziele +1 für jedes zusätzliche Ziel' : null}
                            </div>
                            <div>
                                {Array.from(s_chars.value.entries())
                                    .filter(([, char]) => {
                                        return targets.includes(char.id)
                                    })
                                    .map(([, char]) => {
                                        return char.name
                                    }).join(', ')
                                }
                            </div>
                        </Item> : null}
                    </Grid> : null}

            </Grid>
        </Paper>
    );
};

export default RollerContent;